import { Link } from 'react-router';
import type { KpiTile } from '#shared/types';
import { cn } from '#client/lib/cn';
import { formatCount, formatMoney } from '#client/lib/format';
import { Sparkline } from './Sparkline';

/**
 * One dashboard KPI.
 *
 * Two rules earn their place here:
 *
 *   * **Never render a bare zero.** "0" beside "Past due" reads as a missing
 *     figure or a broken query. "Nothing overdue" reads as the good news it is.
 *     The server supplies the wording per tile.
 *
 *   * **Every tile drills through.** A number an operator cannot act on is
 *     decoration; each one links to the worklist filtered to exactly the rows
 *     it counted.
 *
 * Tone is carried by a 3px rule on the inline-start edge rather than by tinting
 * the whole tile, so six of them side by side stay legible instead of turning
 * the dashboard into a traffic light.
 */
export function StatTile({ tile }: { tile: KpiTile }) {
  const isZero = tile.value === 0;
  const display = tile.is_currency ? formatMoney(tile.value) : formatCount(tile.value);

  return (
    <Link to={tile.href} className={cn('c-stat', `c-stat--${tile.tone}`)}>
      <span className="c-stat__label">{tile.label}</span>

      <span className={cn('c-stat__value', isZero && 'c-stat__value--zero')}>
        {isZero ? tile.zero_label : display}
      </span>

      {tile.spark.length > 1 && (
        <span className="c-stat__spark">
          <Sparkline values={tile.spark} label={`${tile.label}, last 7 days`} />
        </span>
      )}
    </Link>
  );
}
